import React, { useMemo, useCallback, useState, KeyboardEvent } from 'react';
import mapValues from 'lodash/mapValues';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useFormikContext } from 'formik';
import { ValueType, MultiValueProps, OptionProps } from 'react-select';
import { Trans } from 'react-i18next';
import { Chip } from '@app/core';
import {
  CreatableAutoComplete,
  CreatableAutoCompleteProps,
} from './CreatableAutoComplete';

const colors = ['green', 'blue', 'purple', 'yellow', 'red'];
export const getNextTagColor = (tags: Array<{ color?: string }>) => {
  const colorCounts: { [color: string]: number } = colors.reduce(
    (acc, color) => ({ ...acc, [color]: 0 }),
    {},
  );
  tags.forEach((tag: { color?: string }) => {
    if (!tag.color) {
      return;
    }
    if (!colorCounts[tag.color]) {
      colorCounts[tag.color] = 1;
    }
    colorCounts[tag.color]++;
  });

  let minVal = 0;
  let minColor = '';
  mapValues(colorCounts, (val, key) => {
    if (!minColor) {
      minColor = key;
      minVal = val;
      return;
    }
    if (val < minVal) {
      minVal = val;
      minColor = key;
    }
  });

  return minColor;
};

export interface AutocompleteTag {
  id?: string;
  name: string;
  color: string;
  key?: string;
  isNew?: boolean;
  deletedAt?: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  chip: {
    marginRight: theme.marginBase,
    marginTop: theme.marginBase / 2,
  },
  removeIcon: {
    cursor: 'pointer',
    paddingLeft: theme.marginBase / 2,
    paddingRight: theme.marginBase / 2,
    marginRight: -theme.marginBase / 2,
    height: '100%',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
  },
  optionContainer: {
    ...theme.fonts.label,
    color: theme.colors.inkSecondary,
    width: '100%',
    height: '100%',
    marginTop: theme.marginBase,
  },
}));

interface Props {
  name?: string;
  options: AutocompleteTag[];
  styles?: Object;
  creatable?: boolean;
}

const getOptionValue = (tag: AutocompleteTag): string =>
  tag.id || tag.key || `${Math.random()}`;

const getOptionLabel = (tag: AutocompleteTag): string => tag.name;

const isOptionSelected = (
  tag: AutocompleteTag,
  options: readonly AutocompleteTag[],
) =>
  !!options.find(
    (ite: AutocompleteTag) => getOptionValue(ite) === getOptionValue(tag),
  );

const Option = (
  props: OptionProps<AutocompleteTag, true> & { children?: React.ReactNode },
) => {
  const tag = props.data;
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  return (
    <div
      className={classes.optionContainer}
      {...props.innerProps}
      key={getOptionValue(tag)}
    >
      {tag.isNew ? <Trans i18nKey="autocomplete.createTagPrefix" /> : ''}
      <Chip customColor={tag.color}>{props.children}</Chip>
    </div>
  );
};

const MultiValue = (props: MultiValueProps<AutocompleteTag>) => {
  const tag = props.data;
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  return (
    <Chip
      {...props.innerProps}
      customColor={tag.color}
      key={getOptionValue(tag)}
      className={classes.chip}
    >
      {tag.name}
      <div className={classes.removeIcon} {...props.removeProps}>
        <i className="ri-close-line" />
      </div>
    </Chip>
  );
};
const components = { MultiValue, Option };

const defaultStyles = {
  menu: (base: any) => ({
    ...base,
    position: 'relative',
    zIndex: 2,
    paddingLeft: 16,
    paddingBottom: 16,
  }),
};
const getNewTag = (val: string, tags: AutocompleteTag[]) => ({
  name: val,
  color: getNextTagColor(tags),
  key: `${Math.random()}`,
  isNew: true,
});

export const CreatableTagsAutocomplete = (
  props: Props & CreatableAutoCompleteProps<AutocompleteTag, true>,
) => {
  const { options, name, creatable } = props;
  const notCreatable = creatable === false;

  const formik = useFormikContext<any>();

  let tags: AutocompleteTag[] = [];
  if (props.value) {
    tags = props.value;
  } else if (name) {
    tags = formik.getFieldMeta<Array<AutocompleteTag>>(name).value;
  }
  const [inputValue, setInputValue] = useState('');

  const mergedOptions = useMemo(() => {
    const res = [...options.filter(({ deletedAt }) => !deletedAt)];
    if (!tags || !Array.isArray(tags)) {
      return tags;
    }
    tags.forEach((addedTag: AutocompleteTag) => {
      if (!addedTag || addedTag.id) {
        return;
      }
      res.push({
        ...addedTag,
        isNew: false,
      });
    });
    return res;
  }, [options, tags]);

  const getNewOptionData = useCallback(
    (val: string) => getNewTag(val, mergedOptions),
    [mergedOptions],
  );

  const _onChange = props.onChange;
  const onChange = useCallback(
    (value: ValueType<AutocompleteTag, true>, action?: any) => {
      const newValue =
        (action && action.removedValue
          ? tags.filter(
              (tag) =>
                getOptionValue(tag) !== getOptionValue(action.removedValue),
            )
          : value) || [];

      if (_onChange) {
        _onChange(newValue, action);
      }
      if (!formik || !name) {
        return;
      }

      formik.setFieldValue(name, newValue);
    },
    [formik, name, tags, _onChange],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (!inputValue || notCreatable) return;
      switch (event.key) {
        case ',':
        // @ts-ignore no-fallthrough
        case ';':
          event.preventDefault();
          onChange([...tags, getNewOptionData(inputValue)]);
        // @ts-ignore no-fallthrough
        case 'Enter':
        case 'Tab':
          setInputValue('');
      }
    },
    [onChange, setInputValue, inputValue, getNewOptionData, tags, notCreatable],
  );

  const styles = useMemo(
    () => ({
      ...defaultStyles,
      ...props.styles,
    }),
    [props.styles],
  );
  return (
    <CreatableAutoComplete
      {...props}
      value={tags}
      isMulti
      options={mergedOptions}
      onChange={onChange}
      components={components}
      getOptionLabel={getOptionLabel}
      isOptionSelected={isOptionSelected}
      styles={styles}
      inputValue={inputValue}
      onKeyDown={handleKeyDown}
      onInputChange={setInputValue}
      getNewOptionData={getNewOptionData}
      isValidNewOption={notCreatable ? () => false : undefined}
      getOptionValue={getOptionValue}
    />
  );
};
